import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { tickWorkers } from '@/game/view/tickCache';
import { intentLines, isWorker } from '@/game/view/workerIntent';
import { useFrameStore, useSimFrame } from './app/simFrame';
import { unitWorld } from './HealsLayer';

const TILE_RADIUS = config.hexMap.tile.radius;
const LINE_Y = config.hexMap.tile.thickness + 0.12;
/** Lines drawn at once; more than this aren't drawn. */
const MAX_LINES = 64;
/** `--color-accent`. */
const COLOR = '#7dd3fc';

const noRaycast = () => {};

/**
 * Dashed lines from each selected own worker to the tile it works or is
 * heading for (`intentLines`). One line batch: the targets are picked per
 * tick, the worker ends follow the drawn units between ticks. Own workers
 * only, so nothing in the fog is drawn; never raycast.
 */
export function IntentLines({ selectedUnitIds }: { selectedUnitIds: readonly string[] }) {
  const frame = useSimFrame();
  const store = useFrameStore();
  const { geometry, line } = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(MAX_LINES * 6), 3).setUsage(THREE.DynamicDrawUsage)
    );
    // Distance along each segment, for the dashes: 0 and its length, so every
    // segment starts a fresh dash (`computeLineDistances` would reallocate this).
    geometry.setAttribute(
      'lineDistance',
      new THREE.BufferAttribute(new Float32Array(MAX_LINES * 2), 1).setUsage(THREE.DynamicDrawUsage)
    );
    geometry.setDrawRange(0, 0);
    const material = new THREE.LineDashedMaterial({
      color: COLOR,
      dashSize: 0.14,
      gapSize: 0.1,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      toneMapped: false,
    });
    const line = new THREE.LineSegments(geometry, material);
    line.frustumCulled = false;
    line.raycast = noRaycast;
    return { geometry, line };
  }, []);
  useEffect(
    () => () => {
      geometry.dispose();
      (line.material as THREE.Material).dispose();
    },
    [geometry, line]
  );

  // Per tick: who is drawn a line to where.
  const targets = useMemo(() => {
    const { view: state, playerId, rules, terrain, cityNumbers } = frame;
    const units = selectedUnitIds
      .flatMap((id) => state.units[id] ?? [])
      .filter((u) => isWorker(rules, u, playerId));
    return intentLines(
      { state, rules, terrain, playerId, cityNumbers, index: tickWorkers(state, rules) },
      units
    );
  }, [frame, selectedUnitIds]);

  const scratch = useMemo(() => new THREE.Vector3(), []);
  useFrame(() => {
    const state = store.get().view;
    const position = geometry.getAttribute('position') as THREE.BufferAttribute;
    const distance = geometry.getAttribute('lineDistance') as THREE.BufferAttribute;
    let n = 0;
    for (const { id, to } of targets) {
      const unit = state.units[id];
      if (!unit || n >= MAX_LINES) continue;
      unitWorld(unit, LINE_Y, scratch);
      position.setXYZ(n * 2, scratch.x, LINE_Y, scratch.z);
      const end = axialToWorld(to, TILE_RADIUS);
      position.setXYZ(n * 2 + 1, end.x, LINE_Y, end.z);
      distance.setX(n * 2, 0);
      distance.setX(n * 2 + 1, Math.hypot(end.x - scratch.x, end.z - scratch.z));
      n++;
    }
    if (n === 0 && geometry.drawRange.count === 0) return;
    geometry.setDrawRange(0, n * 2);
    position.needsUpdate = true;
    distance.needsUpdate = true;
  });

  return <primitive object={line} />;
}
